import { Camera, CircleAlert, CircleCheck, FileText, Loader2, Upload, X } from "lucide-react";
import * as React from "react";
import { cn } from "../lib/utils.js";
import { Button } from "./button.js";

/**
 * UploadField: presentational, driven by the caller's upload queue.
 * Every string arrives translated; react-ui holds no copy.
 *
 * - "Choose file" (never `capture`) and, on touch devices only, "Take photo"
 *   (`capture="environment"` by default, `"user"` for a selfie). With camera permission denied, Take photo is
 *   hidden and one line says so.
 * - Limits are shown before picking. Each row shows its name and state:
 *   preparing, ready, a progressbar named "{name}, uploading {n}%", done,
 *   failed (with Retry) or invalid (with its error), plus Remove.
 */

export type UploadFieldRowStatus = "preparing" | "ready" | "uploading" | "done" | "failed" | "invalid";

export interface UploadFieldRow {
  id: string;
  name: string;
  status: UploadFieldRowStatus;
  /** 0–100 while uploading. */
  progress: number;
  /** Translated status line ("Preparing a.heic…", "a.jpg uploaded", …). */
  statusText: string;
  /** Translated accessible name of the progressbar while uploading. */
  progressLabel?: string;
  /** Translated per-file error (invalid rows). */
  errorText?: string;
  removeLabel: string;
  /** Present on failed rows. */
  retryLabel?: string;
}

export interface UploadFieldProps {
  /** Visible group label, e.g. "Photos of your item". */
  label: string;
  /** Limits line, shown before picking. */
  limits: string;
  accept: string;
  chooseFileLabel: string;
  takePhotoLabel: string;
  /** Show "Take photo" (touch devices only). */
  canCapture: boolean;
  /** The camera "Take photo" opens: the back camera (default) or the front one (a selfie). */
  capture?: "environment" | "user";
  /** Camera permission denied: the line shown instead of Take photo. */
  cameraDeniedText?: string;
  rows: UploadFieldRow[];
  /** Field-level error (e.g. "Add at least 1 photo", "You can add up to 10 files"). */
  error?: string;
  /** Extra content under the controls (e.g. the re-attach list). */
  children?: React.ReactNode;
  /** Submitting: no files can be added, removed or retried. */
  locked?: boolean;
  onFiles: (files: File[]) => void;
  onRemove: (id: string) => void;
  onRetry: (id: string) => void;
  testId?: string;
}

function StatusIcon({ status }: { status: UploadFieldRowStatus }) {
  const cls = "size-5 shrink-0";
  switch (status) {
    case "preparing":
    case "uploading":
      return <Loader2 aria-hidden="true" className={cn(cls, "animate-spin text-muted-foreground")} />;
    case "done":
      return <CircleCheck aria-hidden="true" className={cn(cls, "text-status-success")} />;
    case "failed":
    case "invalid":
      return <CircleAlert aria-hidden="true" className={cn(cls, "text-status-error")} />;
    default:
      return <FileText aria-hidden="true" className={cn(cls, "text-muted-foreground")} />;
  }
}

function UploadField({
  label,
  limits,
  accept,
  chooseFileLabel,
  takePhotoLabel,
  canCapture,
  capture = "environment",
  cameraDeniedText,
  rows,
  error,
  children,
  locked = false,
  onFiles,
  onRemove,
  onRetry,
  testId,
}: UploadFieldProps) {
  const id = React.useId();
  const labelId = `${id}-label`;
  const limitsId = `${id}-limits`;
  const errorId = `${id}-error`;
  const chooseRef = React.useRef<HTMLInputElement>(null);
  const captureRef = React.useRef<HTMLInputElement>(null);
  const showCapture = canCapture && !cameraDeniedText;
  const describedBy = [limitsId, error ? errorId : null].filter(Boolean).join(" ");

  const pick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    // Reset so picking the same file again fires change.
    e.target.value = "";
    if (files.length) onFiles(files);
  };

  return (
    <div role="group" aria-labelledby={labelId} aria-describedby={describedBy} data-testid={testId} className="flex flex-col gap-3">
      <p id={labelId} className="font-semibold">
        {label}
      </p>
      <p id={limitsId} className="text-sm text-muted-foreground">
        {limits}
      </p>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="touch"
          aria-disabled={locked ? true : undefined}
          onClick={() => !locked && chooseRef.current?.click()}
        >
          <Upload aria-hidden="true" />
          {chooseFileLabel}
        </Button>
        {showCapture ? (
          <Button
            type="button"
            variant="outline"
            size="touch"
            aria-disabled={locked ? true : undefined}
            onClick={() => !locked && captureRef.current?.click()}
          >
            <Camera aria-hidden="true" />
            {takePhotoLabel}
          </Button>
        ) : null}
        <input
          ref={chooseRef}
          type="file"
          accept={accept}
          multiple
          disabled={locked}
          className="sr-only"
          tabIndex={-1}
          aria-hidden="true"
          data-testid={testId ? `${testId}-choose` : undefined}
          onChange={pick}
        />
        {showCapture ? (
          <input
            ref={captureRef}
            type="file"
            accept="image/*"
            capture={capture}
            disabled={locked}
            className="sr-only"
            tabIndex={-1}
            aria-hidden="true"
            data-testid={testId ? `${testId}-capture` : undefined}
            onChange={pick}
          />
        ) : null}
      </div>
      {canCapture && cameraDeniedText ? <p className="text-sm text-muted-foreground">{cameraDeniedText}</p> : null}
      {error ? (
        <p id={errorId} className="text-sm font-semibold text-status-error">
          {error}
        </p>
      ) : null}
      {children}
      {rows.length ? (
        <ul className="flex flex-col divide-y rounded-lg border">
          {rows.map((row) => (
            <li key={row.id} data-status={row.status} className="flex flex-col gap-2 px-3 py-2">
              <div className="flex items-center gap-3">
                <StatusIcon status={row.status} />
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-sm font-semibold">{row.name}</span>
                  {row.status !== "ready" ? <span className="text-sm text-muted-foreground">{row.statusText}</span> : null}
                  {row.errorText ? <span className="text-sm font-semibold text-status-error">{row.errorText}</span> : null}
                </div>
                {row.retryLabel ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="touch"
                    aria-disabled={locked ? true : undefined}
                    onClick={() => !locked && onRetry(row.id)}
                  >
                    {row.retryLabel}
                  </Button>
                ) : null}
                <Button
                  type="button"
                  variant="ghost"
                  size="touch"
                  aria-label={row.removeLabel}
                  aria-disabled={locked ? true : undefined}
                  onClick={() => !locked && onRemove(row.id)}
                >
                  <X aria-hidden="true" />
                </Button>
              </div>
              {row.status === "uploading" ? (
                <div
                  role="progressbar"
                  aria-label={row.progressLabel}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={row.progress}
                  className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
                >
                  <div className="h-full bg-primary transition-[width]" style={{ width: `${row.progress}%` }} />
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export { UploadField };
